<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Pagination,
  Section,
  Select,
  Spinner,
  Table,
  Tabs,
  Toolbar,
} from '@drama/ui'
import { Eye, SlidersHorizontal } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import { fetchModelCapabilityCatalog, fetchProviderCapabilityKeys } from '../../api/model-capabilities.js'
import { fetchUsageFacets, listUsageCalls } from '../../api/usage.js'
import { buildCallQuery, callFiltersFromQuery, emptyCallFilters, NO_KEY, TIME_WINDOWS } from './call-filters.js'
import { MODEL_CAPABILITIES } from './model-capability.js'
import { formatDateTime, formatDuration, formatInt, formatMoney, formatOptionalInt } from './llm-format.js'
import { videoSupplierCostState, videoSupplierCostText } from '../../lib/video-supplier-cost.js'
import CallDetailDrawer from './call/CallDetailDrawer.vue'
import CapabilityCallDrawer from './call/CapabilityCallDrawer.vue'
import MediaPreviewModal from './call/MediaPreviewModal.vue'
import StoryCallBusiness from './call/StoryCallBusiness.vue'

const PAGE_SIZES = [20, 50, 100]
const CAPABILITIES = MODEL_CAPABILITIES
const BILLING_MODES = ['record_only', 'fixed_points', 'cost_ratio', 'legacy_record_only', 'legacy_points']
const COST_STATES = ['all', 'priced', 'unpriced', 'charged']
const STORY_TASKS = ['narrative_scene', 'narrative_record', 'memory', 'director', 'player_suggestions']
const BUSINESS_NS = 'visualGameAdmin.storyCallBusiness'

const { t } = useI18n()
const route = useRoute()
const filters = ref(emptyCallFilters())
const rows = ref([])
const total = ref(0)
const loading = ref(false)
const error = ref('')
const pageSize = ref(PAGE_SIZES[0])
const page = ref(1)
const models = ref([])
const providerKeys = ref([])
const keyPoolError = ref('')
const selectedId = ref('')
const selectedCapability = ref('text')
const drawerOpen = ref(false)
const previewOpen = ref(false)
const previewUrl = ref('')
const previewCapability = ref('image')
const previewTitle = ref('')
const advancedOpen = ref(false)

const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
const pageSummary = computed(() => t('visualGameAdmin.llm.calls.pagination.summary', {
  total: total.value.toLocaleString(),
  page: page.value,
  pages: pageCount.value,
}))
const byCallId = computed(() => filters.value.requestId !== '')
const textOnly = computed(() => filters.value.capability === 'text')
/** 向量调用不向玩家计费，embedding_call_log 没有 billing_mode / charged_points 这两列。 */
const billed = computed(() => filters.value.capability !== 'embedding')
const windowOptions = computed(() => TIME_WINDOWS.map(value => ({
  value,
  label: t(`visualGameAdmin.llm.calls.filters.windows.${value}`),
})))
const capabilityTabs = computed(() => CAPABILITIES.map(value => ({
  key: value,
  label: t(`visualGameAdmin.llm.calls.capability.${value}`),
})))
const modelOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.calls.filters.all') },
  ...models.value
    .filter(item => item.capability === filters.value.capability)
    .map(item => ({
      value: JSON.stringify([item.provider, item.model]),
      label: `${item.provider_display_name} · ${item.provider_capability_code} · ${item.model}`,
    })),
])
const selectedModel = computed({
  get() {
    if (filters.value.model === '') return ''
    return JSON.stringify([filters.value.provider, filters.value.model])
  },
  set(value) {
    if (value === '') {
      filters.value.provider = ''
      filters.value.model = ''
      return
    }
    const [provider, model] = JSON.parse(value)
    filters.value.provider = provider
    filters.value.model = model
  },
})
const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.calls.filters.all') },
  ...['ok', 'error', 'aborted', 'failed'].map(value => ({
    value,
    label: t(`visualGameAdmin.llm.calls.status.${value}`),
  })),
])
const sourceOptions = computed(() => [
  { value: '', label: t(`${BUSINESS_NS}.all_sources`) },
  { value: 'story', label: t(`${BUSINESS_NS}.story`) },
])
const businessOptions = computed(() => [
  { value: '', label: t(`${BUSINESS_NS}.all_outcomes`) },
  { value: 'failed', label: t(`${BUSINESS_NS}.failed_only`) },
])
const storyTaskOptions = computed(() => [
  { value: '', label: t(`${BUSINESS_NS}.all_tasks`) },
  ...STORY_TASKS.map(value => ({ value, label: t(`${BUSINESS_NS}.tasks.${value}`) })),
])
const billingOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.calls.filters.all') },
  ...BILLING_MODES.map(value => ({ value, label: t(`visualGameAdmin.llm.calls.billingMode.${value}`) })),
])
const costStateOptions = computed(() => COST_STATES
  .filter(value => billed.value || value !== 'charged')
  .map(value => ({
    value,
    label: t(`visualGameAdmin.llm.calls.costState.${value}`),
  })))
const keyOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.llm.calls.filters.all') },
  { value: NO_KEY, label: t('visualGameAdmin.llm.calls.key.noKeyLeased') },
  ...providerKeys.value.map(key => ({
    value: key.id,
    label: `${key.provider} · ${key.label ?? t('visualGameAdmin.llm.calls.key.noLabel')} · ${key.api_key_masked}`,
  })),
])
const columns = computed(() => [
  { key: 'time', label: t('visualGameAdmin.llm.calls.table.time'), width: '160px', mono: true, wrap: true },
  { key: 'model', label: t('visualGameAdmin.llm.calls.table.model'), width: '300px', wrap: true },
  { key: 'status', label: t(`${BUSINESS_NS}.model_status`), width: '180px', wrap: true },
  ...(textOnly.value ? [{ key: 'business', label: t(`${BUSINESS_NS}.outcome`), width: '260px', wrap: true }] : []),
  { key: 'usage', label: t('visualGameAdmin.llm.calls.table.usage'), width: '215px', mono: true, wrap: true },
  { key: 'accounting', label: t('visualGameAdmin.llm.calls.table.accounting'), width: '245px', wrap: true },
  { key: 'attribution', label: t('visualGameAdmin.llm.calls.table.attribution'), width: '155px', wrap: true },
  { key: 'output', label: t('visualGameAdmin.llm.calls.table.preview'), width: '86px' },
  { key: 'actions', label: t('visualGameAdmin.llm.calls.table.open'), width: '68px' },
])
const tableRows = computed(() => rows.value.map(row => ({
  ...row,
  time: formatDateTime(row.created_at),
  statusLabel: t(`visualGameAdmin.llm.calls.status.${row.status}`),
  providerIdentity: `${row.provider_display_name} · ${row.provider_capability_code}`,
  usage: usageText(row),
  videoInput: videoInputText(row),
  error: errorText(row),
  cost: row.capability === 'video' ? videoSupplierCostText(row, t) : row.supplier_cost_micros === null ? '—' : formatMoney(row.supplier_cost_micros),
  costFormula: videoCostFormula(row),
  billing: billingText(row),
  duration: row.duration_ms === null ? '—' : formatDuration(row.duration_ms),
  user: row.user_id ?? '—',
})))

onMounted(async () => {
  filters.value = callFiltersFromQuery(route.query)
  advancedOpen.value = filters.value.userId !== ''
    || filters.value.sessionId !== ''
    || filters.value.requestId !== ''
    || filters.value.providerRequestId !== ''
    || filters.value.keyId !== ''
    || filters.value.story_task !== ''
    || filters.value.story_query !== ''
  if (filters.value.requestId !== '') {
    selectedId.value = filters.value.requestId
    selectedCapability.value = filters.value.capability
    drawerOpen.value = true
  }
  await loadProviderKeys()
  await search()
})

async function loadFacets() {
  const query = buildCallQuery(filters.value, { limit: 1, offset: 0 })
  if (!query.since) query.since = new Date(Date.now() - 30 * 86_400_000).toISOString()
  delete query.provider
  delete query.model
  const facets = await fetchUsageFacets(query)
  models.value = facets.models
}

async function onCapabilityChange(capability) {
  filters.value.capability = capability
  filters.value.provider = ''
  filters.value.model = ''
  filters.value.keyId = ''
  filters.value.sessionId = ''
  for (const key of ['source', 'business_status', 'story_task', 'story_query']) filters.value[key] = ''
  // 这两个筛选项只有计费能力认。留着切到「向量」页签，服务端当场判成非法查询。
  if (capability === 'embedding') {
    filters.value.billingMode = ''
    if (filters.value.costState === 'charged') filters.value.costState = 'all'
  }
  await Promise.all([loadProviderKeys(), search()])
}

async function loadProviderKeys() {
  providerKeys.value = []
  keyPoolError.value = ''
  if (!textOnly.value) return
  try {
    const catalog = await fetchModelCapabilityCatalog()
    const providersById = new Map(catalog.providers.map(provider => [provider.id, provider]))
    const keyPoolCapabilities = catalog.text.provider_capabilities.filter(providerCapability => (
      providerCapability.credential_source === 'key_pool'
    ))
    const keyPools = await Promise.all(keyPoolCapabilities.map(async providerCapability => ({
      provider_capability: providerCapability,
      ...await fetchProviderCapabilityKeys(providerCapability.id),
    })))
    providerKeys.value = keyPools.flatMap(keyPool => keyPool.keys.map(key => ({
      ...key,
      provider: `${providersById.get(keyPool.provider_capability.provider_id).display_name} · ${keyPool.provider_capability.code}`,
    })))
  } catch (requestError) {
    keyPoolError.value = requestError.message
  }
}

async function search() {
  page.value = 1
  await Promise.all([load(), loadFacets()])
}

async function reset() {
  filters.value = emptyCallFilters()
  advancedOpen.value = false
  await Promise.all([loadProviderKeys(), search()])
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const response = await listUsageCalls(buildCallQuery(filters.value, {
      limit: pageSize.value,
      offset: (page.value - 1) * pageSize.value,
    }))
    rows.value = response.items
    total.value = response.total
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function changePageSize(value) {
  pageSize.value = value
  page.value = 1
  return load()
}

function nextPage() {
  page.value += 1
  return load()
}

function prevPage() {
  page.value -= 1
  return load()
}

function widenWindow() {
  filters.value.window = '7d'
  return search()
}

function open(row) {
  selectedId.value = callId(row)
  selectedCapability.value = row.capability
  drawerOpen.value = true
}

function openLlm(requestId) {
  selectedId.value = requestId
  selectedCapability.value = 'text'
  drawerOpen.value = true
}

function openPreview(call) {
  previewUrl.value = call.output_url
  previewCapability.value = call.capability
  previewTitle.value = `${t(`visualGameAdmin.llm.calls.capability.${call.capability}`)} · ${call.model}`
  previewOpen.value = true
}

function usageText(row) {
  switch (row.capability) {
    case 'text':
      return [
        `${t('visualGameAdmin.llm.calls.usage.input')} ${formatOptionalInt(row.prompt_tokens)}`,
        `${t('visualGameAdmin.llm.calls.usage.output')} ${formatOptionalInt(row.completion_tokens)}`,
        `${t('visualGameAdmin.llm.calls.usage.cached')} ${formatOptionalInt(row.cached_tokens)}`,
        `${t('visualGameAdmin.llm.calls.usage.cacheWrite')} ${formatOptionalInt(row.cache_write_tokens)}`,
      ].join(' · ')
    case 'image':
      if (row.generated_images === null || row.total_tokens === null) return '—'
      return `${formatInt(row.generated_images)} ${t('visualGameAdmin.llm.calls.usage.images')} · ${formatInt(row.total_tokens)} tokens`
    case 'video':
      return row.total_tokens === null ? '—' : `${formatInt(row.total_tokens)} tokens`
    case 'tts':
      return row.billed_characters === null ? '—' : `${formatInt(row.billed_characters)} ${t('visualGameAdmin.llm.calls.usage.characters')}`
    case 'embedding':
      if (row.total_tokens === null) return '—'
      // 维度一起报：切了向量模型之后，这一列直接说明这次调用算在哪个向量空间。
      return `${formatInt(row.total_tokens)} tokens · ${formatInt(row.dimensions)} ${t('visualGameAdmin.llm.calls.usage.dimensions')}`
    default:
      throw new Error(`Unhandled model capability: ${row.capability}`)
  }
}

function videoInputText(row) {
  if (row.capability !== 'video' || row.video_pricing === null) return null
  return t(`visualGameAdmin.llm.calls.usage.${row.video_pricing.input_mode}`)
}

function videoCostFormula(row) {
  if (row.capability !== 'video' || row.video_pricing === null || videoSupplierCostState(row) !== 'recorded_estimate_not_supplier_invoice') return null
  return t('visualGameAdmin.llm.calls.usage.videoCostFormula', {
    tokens: formatInt(row.total_tokens),
    rate: formatMoney(row.video_pricing.applied_micros_per_m),
  })
}

function callId(row) {
  switch (row.capability) {
    case 'text':
      return row.request_id
    case 'image':
    case 'video':
    case 'tts':
    case 'embedding':
      return row.id
    default:
      throw new Error(`Unhandled model capability: ${row.capability}`)
  }
}

function errorText(row) {
  return row.capability === 'text' ? row.error_message : row.error_reason
}

function billingText(row) {
  // 向量调用这一列压根不存在，不是「没计费」而是「不适用」。
  if (row.billing_mode === undefined || row.billing_mode === null) return '—'
  const points = row.charged_points === null ? '—' : formatInt(row.charged_points)
  return `${t(`visualGameAdmin.llm.calls.billingMode.${row.billing_mode}`)} · ${points}`
}
</script>

<template>
  <div class="calls vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.calls.title')" />

    <Section class="calls__tabs">
      <Tabs
        :model-value="filters.capability"
        :tabs="capabilityTabs"
        @update:model-value="onCapabilityChange"
      />
    </Section>

    <Section>
      <Toolbar>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.model') }}</label>
          <Select v-model="selectedModel" :options="modelOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.window') }}</label>
          <Select v-model="filters.window" :options="windowOptions" :disabled="byCallId" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t(`${BUSINESS_NS}.model_status`) }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <template v-if="textOnly">
          <div class="drama-toolbar-field">
            <label>{{ t(`${BUSINESS_NS}.source`) }}</label>
            <Select v-model="filters.source" :options="sourceOptions" :aria-label="t(`${BUSINESS_NS}.source`)" size="sm" data-testid="call-source-filter" />
          </div>
          <div class="drama-toolbar-field">
            <label>{{ t(`${BUSINESS_NS}.outcome`) }}</label>
            <Select v-model="filters.business_status" :options="businessOptions" :aria-label="t(`${BUSINESS_NS}.outcome`)" size="sm" data-testid="call-business-filter" />
          </div>
        </template>
        <div v-if="billed" class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.billingMode') }}</label>
          <Select v-model="filters.billingMode" :options="billingOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.llm.calls.filters.costState') }}</label>
          <Select v-model="filters.costState" :options="costStateOptions" size="sm" />
        </div>

        <template #actions>
          <Button variant="secondary" size="sm" @click="advancedOpen = !advancedOpen">
            <SlidersHorizontal :size="14" />
            {{ t(`visualGameAdmin.llm.calls.filters.${advancedOpen ? 'less' : 'more'}`) }}
          </Button>
          <Button size="sm" :loading="loading" @click="search">{{ t('visualGameAdmin.llm.calls.filters.apply') }}</Button>
          <Button variant="ghost" size="sm" @click="reset">{{ t('visualGameAdmin.llm.calls.filters.reset') }}</Button>
        </template>

        <template v-if="advancedOpen || filters.window === 'custom'" #advanced>
          <div v-if="filters.window === 'custom'" class="drama-toolbar-field drama-toolbar-field--wide">
            <label>{{ t('visualGameAdmin.llm.calls.filters.since') }}</label>
            <Input v-model="filters.since" type="datetime-local" :disabled="byCallId" size="sm" />
          </div>
          <div v-if="filters.window === 'custom'" class="drama-toolbar-field drama-toolbar-field--wide">
            <label>{{ t('visualGameAdmin.llm.calls.filters.until') }}</label>
            <Input v-model="filters.until" type="datetime-local" :disabled="byCallId" size="sm" />
          </div>
          <template v-if="advancedOpen">
            <template v-if="textOnly">
              <div class="drama-toolbar-field">
                <label for="calls-story-work">{{ t(`${BUSINESS_NS}.work`) }}</label>
                <Input id="calls-story-work" v-model="filters.story_query" :placeholder="t(`${BUSINESS_NS}.work_placeholder`)" size="sm" />
              </div>
              <div class="drama-toolbar-field">
                <label>{{ t(`${BUSINESS_NS}.task`) }}</label>
                <Select v-model="filters.story_task" :options="storyTaskOptions" :aria-label="t(`${BUSINESS_NS}.task`)" size="sm" />
              </div>
            </template>
            <div v-if="textOnly" class="drama-toolbar-field">
              <label>{{ t('visualGameAdmin.llm.calls.filters.key') }}</label>
              <Select v-model="filters.keyId" :options="keyOptions" size="sm" />
            </div>
            <div class="drama-toolbar-field">
              <label>{{ t('visualGameAdmin.llm.calls.filters.user') }}</label>
              <Input v-model="filters.userId" :placeholder="t('visualGameAdmin.llm.calls.filters.placeholder.user')" size="sm" />
            </div>
            <div v-if="textOnly" class="drama-toolbar-field">
              <label>{{ t('visualGameAdmin.llm.calls.filters.session') }}</label>
              <Input v-model="filters.sessionId" :placeholder="t('visualGameAdmin.llm.calls.filters.placeholder.session')" size="sm" />
            </div>
            <div class="drama-toolbar-field drama-toolbar-field--wide">
              <label>{{ t('visualGameAdmin.llm.calls.filters.requestId') }}</label>
              <Input v-model="filters.requestId" :placeholder="t('visualGameAdmin.llm.calls.filters.placeholder.requestId')" size="sm" />
            </div>
            <div class="drama-toolbar-field drama-toolbar-field--wide">
              <label>{{ t('visualGameAdmin.llm.calls.filters.providerRequestId') }}</label>
              <Input v-model="filters.providerRequestId" :placeholder="t('visualGameAdmin.llm.calls.filters.placeholder.providerRequestId')" size="sm" />
            </div>
          </template>
        </template>
      </Toolbar>
      <p v-if="keyPoolError" class="vga-error calls__filter-error" role="alert">{{ keyPoolError }}</p>
      <p v-if="textOnly" class="calls__business-hint">{{ t(`${BUSINESS_NS}.hint`) }}</p>
    </Section>

    <Section :padded="false">
      <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="search">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <div v-else-if="rows.length === 0" class="vga-state">
        <EmptyState :title="t('visualGameAdmin.llm.calls.empty.title')">
          <template #action><Button @click="widenWindow">{{ t('visualGameAdmin.llm.calls.empty.action') }}</Button></template>
        </EmptyState>
      </div>
      <template v-else>
        <Table
          :columns="columns"
          :rows="tableRows"
          :row-key="filters.capability === 'text' ? 'request_id' : 'id'"
        >
          <template #model="{ row }">
            <span class="calls__model">
              {{ row.providerIdentity }}
              <strong>{{ row.model }}</strong>
            </span>
          </template>
          <template #status="{ row }">
            <div class="calls__status">
              <Badge :variant="row.status === 'ok' ? 'success' : row.status === 'aborted' ? 'warning' : 'danger'">
                {{ row.statusLabel }}
              </Badge>
              <span v-if="row.error">{{ row.error }}</span>
            </div>
          </template>
          <template #usage="{ row }">
            <div class="calls__stacked-cell">
              <strong>{{ row.usage }}</strong>
              <span v-if="row.videoInput">{{ row.videoInput }}</span>
            </div>
          </template>
          <template #business="{ row }">
            <StoryCallBusiness v-if="row.business" :business="row.business" compact />
            <span v-else>—</span>
          </template>
          <template #accounting="{ row }">
            <div class="calls__stacked-cell">
              <strong>{{ row.cost }}</strong>
              <span v-if="row.costFormula">{{ row.costFormula }}</span>
              <span>{{ row.billing }}</span>
            </div>
          </template>
          <template #attribution="{ row }">
            <div class="calls__stacked-cell">
              <strong>{{ row.duration }}</strong>
              <span>{{ row.user }}</span>
            </div>
          </template>
          <template #output="{ row }">
            <Button v-if="row.output_url" variant="ghost" size="sm" @click="openPreview(row)">
              <Eye :size="14" />
              {{ t('visualGameAdmin.llm.calls.table.preview') }}
            </Button>
            <span v-else>—</span>
          </template>
          <template #actions="{ row }"><Button variant="ghost" size="sm" @click="open(row)">{{ t('visualGameAdmin.llm.calls.table.open') }}</Button></template>
        </Table>
      </template>
    </Section>

    <Pagination
      v-if="!loading && !error && rows.length > 0"
      :has-next="page < pageCount"
      :has-prev="page > 1"
      :loading="loading"
      :prev-label="t('visualGameAdmin.llm.calls.pagination.prev')"
      :next-label="t('visualGameAdmin.llm.calls.pagination.next')"
      :summary="pageSummary"
      :page-size="pageSize"
      :page-size-options="PAGE_SIZES"
      :page-size-label="t('visualGameAdmin.llm.calls.pagination.pageSize')"
      @prev="prevPage"
      @next="nextPage"
      @update:page-size="changePageSize"
    />

    <CallDetailDrawer v-if="selectedCapability === 'text'" v-model:open="drawerOpen" :request-id="selectedId" @open="openLlm" />
    <CapabilityCallDrawer
      v-else
      v-model:open="drawerOpen"
      :call-id="selectedId"
      :capability="selectedCapability"
      @preview="openPreview"
    />
    <MediaPreviewModal
      v-model:open="previewOpen"
      :capability="previewCapability"
      :title="previewTitle"
      :url="previewUrl"
    />
  </div>
</template>

<style scoped lang="scss">
.calls {
  &__business-hint {
    margin: var(--drama-space-md) 0 0;
    color: var(--drama-color-text-muted);
    font-size: var(--drama-font-size-xs);
  }
  &__filter-error {
    margin: var(--drama-space-md) 0 0;
    font-size: var(--drama-font-size-xs);
  }

  &__tabs :deep(.drama-tabs__panel) {
    display: none;
  }

  &__status,
  &__stacked-cell {
    display: grid;
    gap: var(--drama-space-xs);
    min-width: 0;
  }

  &__model,
  &__status span,
  &__stacked-cell span {
    color: var(--drama-color-text-muted);
    font-family: var(--drama-font-family-mono);
    font-size: var(--drama-font-size-xs);
    overflow-wrap: anywhere;
    white-space: normal;
  }

  &__model {
    display: grid;
    gap: var(--drama-space-xs);
  }

  &__model strong {
    display: block;
    color: var(--drama-color-text);
    font-weight: var(--drama-font-weight-medium);
    overflow-wrap: anywhere;
    white-space: normal;
  }

  &__stacked-cell strong {
    font-weight: var(--drama-font-weight-medium);
  }
}
</style>
